import api from './client';
import type {
  RegisterTokenRequest,
  RegisterTokenResponse,
  UnregisterTokenRequest,
  UnregisterTokenResponse,
  SendNotificationRequest,
  SendNotificationResponse,
  ListTokensResponse,
} from './types';

export const pushNotificationsApi = {
  /**
   * Register a new FCM token for the authenticated user
   * @param request - Token registration data
   */
  registerToken: async (request: RegisterTokenRequest): Promise<RegisterTokenResponse> => {
    const { data } = await api.post('/push/register', request);
    return data;
  },

  /**
   * Unregister (delete) an FCM token for the authenticated user
   * @param request - Token unregistration data
   */
  unregisterToken: async (request: UnregisterTokenRequest): Promise<UnregisterTokenResponse> => {
    const { data } = await api.post('/push/unregister', request);
    return data;
  },

  /**
   * Get list of user's registered tokens
   */
  listTokens: async (): Promise<ListTokensResponse> => {
    const { data } = await api.get('/push/tokens');
    return data;
  },

  /**
   * Send push notification to specific users or tokens
   * Note: Not recommended for direct use. Prefer using backend endpoints.
   * @param request - Send notification request
   */
  send: async (request: SendNotificationRequest): Promise<SendNotificationResponse> => {
    const { data } = await api.post('/push/send', request);
    return data;
  },

  /**
   * Mark a sent push notification as opened by the current user.
   * Idempotent — subsequent calls return ``already_opened: true``.
   * @param notificationId - The id returned in the FCM data payload as ``notification_id``.
   */
  markOpened: async (notificationId: string): Promise<{
    notification_id: string;
    user_id: string;
    already_opened: boolean;
  }> => {
    const { data } = await api.post(`/push/sent/${notificationId}/opened`);
    return data;
  },

  /**
   * List recent sent notifications for the current org, with open-tracking stats.
   * @param params - Optional filters (category, limit).
   */
  listSent: async (params?: {
    category?: string;
    limit?: number;
  }): Promise<{
    notifications: Array<{
      id: string;
      title: string;
      body: string;
      data: Record<string, string>;
      user_ids: string[];
      team_user_ids: string[];
      category?: string;
      org_id?: string;
      project_id?: string;
      sender_user_id?: string;
      token_count: number;
      success_count: number;
      failure_count: number;
      sent_at: string;
      opened_by: string[];
      opened_at: Record<string, string>;
    }>;
    total: number;
  }> => {
    const { data } = await api.get('/push/sent', { params });
    return data;
  },
};
